import { supabase } from '@/lib/supabase'
import type { ProjectType, ProjectQuestion } from '@/config/project-questions'

export interface AnalyzeProjectResponse {
  completeness_score: number // 0-1 (0.75 = 75%)
  missing_criteria: {
    category: string
    criteria: string
    importance: 'high' | 'medium' | 'low'
  }[]
  next_questions: ProjectQuestion[]
  is_complete: boolean
  can_skip: boolean
}

export interface GenerateBMADResponse {
  bmad_markdown: string
  bmad_json: Record<string, unknown>
}

const API_URL = import.meta.env.VITE_EMAIL_API_URL || 'http://localhost:3001'

async function getAuthHeaders(): Promise<HeadersInit> {
  const { data: { session } } = await supabase.auth.getSession()
  return {
    'Content-Type': 'application/json',
    ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {}),
  }
}

/**
 * Map question types from backend format to frontend format.
 * The backend may return 'open' which the frontend renders as 'textarea'.
 */
function mapQuestionTypes(questions: ProjectQuestion[]): ProjectQuestion[] {
  return questions.map((q) => ({
    ...q,
    type: q.type === 'open' ? 'textarea' : q.type,
  }))
}

/**
 * Analyse les réponses du projet et génère les questions suivantes
 */
export async function analyzeProjectRequest(
  projectType: ProjectType,
  initialAnswers: Record<string, unknown>,
  iteration: number,
  previousQuestions?: ProjectQuestion[]
): Promise<AnalyzeProjectResponse> {
  try {
    const headers = await getAuthHeaders()
    const response = await fetch(`${API_URL}/api/ai/analyze-project-request`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        project_type: projectType,
        initial_answers: initialAnswers,
        iteration,
        previous_questions: previousQuestions || [],
      }),
    })

    if (!response.ok) {
      throw new Error(`Backend error: ${response.status}`)
    }

    const json = await response.json()
    const data = json.success ? json.data : json

    // Validation
    if (
      typeof data.completeness_score !== 'number' ||
      !Array.isArray(data.missing_criteria) ||
      !Array.isArray(data.next_questions)
    ) {
      throw new Error('Format de réponse backend invalide')
    }

    return {
      ...data,
      next_questions: mapQuestionTypes(data.next_questions),
    }
  } catch (error) {
    console.error('Erreur analyse projet IA:', error)

    // Fallback en cas d'erreur
    return {
      completeness_score: 0.5,
      missing_criteria: [
        {
          category: 'general',
          criteria: 'Informations générales incomplètes',
          importance: 'medium',
        },
      ],
      next_questions: [
        {
          id: `q_iteration${iteration}_fallback`,
          type: 'textarea',
          question: 'Pouvez-vous nous donner plus de détails sur votre projet ?',
          category: 'general',
        },
      ],
      is_complete: false,
      can_skip: true,
    }
  }
}

/**
 * Génère le document BMAD final en Markdown
 */
export async function generateBMADDocument(
  projectType: ProjectType,
  allAnswers: Record<string, unknown>
): Promise<GenerateBMADResponse> {
  try {
    const headers = await getAuthHeaders()
    const response = await fetch(`${API_URL}/api/ai/generate-bmad-markdown`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        project_type: projectType,
        all_answers: allAnswers,
      }),
    })

    if (!response.ok) {
      throw new Error(`Backend error: ${response.status}`)
    }

    const json = await response.json()
    const data = json.success ? json.data : json

    return data as GenerateBMADResponse
  } catch (error) {
    console.error('Erreur génération BMAD:', error)

    // Fallback
    return {
      bmad_markdown: `# Document BMAD - ${projectType}\n\n_Erreur lors de la génération du document._`,
      bmad_json: { error: 'Generation failed' },
    }
  }
}

export const projectAIService = {
  analyzeProjectRequest,
  generateBMADDocument,
}
